<script lang="ts">
	import {
		CourseLightModel,
		CourseModel,
		SessionLightModel,
		SessionModel,
		stringToSlug
	} from '@24hbs/utils';
	import type { PageData } from './$types';
	import type { FaqDataCourse } from '$lib/utils/faq/types';
	import { useHistoryStore } from '$lib/utils/history-store';
	import { useCTAFlow } from '$lib/utils/cta-flow';
	import { useCountdown } from '$lib/utils/countdown-store';
	import { handleUrlClientSide } from '$lib/utils/assets-utils';
	import { browser } from '$app/environment';
	import {
		cleanCourseHtml,
		removeMasterCorso
	} from '$lib/utils/formatter';
	import { GtmStore } from '$lib/utils/gtm-store';
	import { ctaRequestBuilders } from '$lib/utils/cta-flow/helpers';
	import { page } from '$app/stores';
	import {
		courseHasCFUCourse,
		getCourseWithCFUs,
		getCourseWithoutCFUs,
		isCFUCourse
	} from '$lib/utils/courses/cfu-courses';
	import {
		getCourseByProductCode,
		hasVatFreeSession,
		isEuroconferenceSession,
		isMarconiSession
	} from '$lib/utils/course-utils';
	import { goto } from '$app/navigation';
	import { onMount } from 'svelte';
	import { customPageOptions } from '$lib/utils/layout-store';
	import { StructureUtils } from '$lib/utils/structured-utils';
	import { PUBLIC_FTOGGLE_PRODUCT_SOCIAL_RATING } from '$env/static/public';
	import { Breadcrumb, BreadcrumbItem } from '$lib/components/molecules/Breadcrumb';
	import { HeroProduct } from '$lib/components/organisms/Hero';
	import {
		FeatureBoxesByCourseType,
		Grid,
		SideCourseCart,
		SideCourseData
	} from '$lib/components/organisms/Layout';
	import { AnchorTabs } from '$lib/components/atoms/AnchorTabs';
	import type { ICourseCategoryFields, ICourseTypeFields } from '$lib/utils/contentful/types';
	import {
		CourseCertificates,
		CourseFeatures,
		CourseLearningFeatures,
		CoursePartners,
		MosaicCourseModules,
		MosaicCoursePartners,
		MosaicCourseReviews,
		SectionCertificates,
		SectionFaq,
		SectionProduct
	} from '$lib/components/organisms/Section';
	import {
		CoursePackageTableDesktop,
		CoursePackageTableMobile
	} from '$lib/components/organisms/Table';
	import { CourseScheduleSelector } from '$lib/components/organisms/Schedule';
	import {
		CertificateCard,
		CourseModuleCard,
		CoursePartnerCard,
		CourseReviewCard
	} from '$lib/components/organisms/Card';
	import {
		SectionCarousel,
		SectionCarouselIndicators,
		SectionCarouselSlides
	} from '$lib/components/organisms/SectionCarousel';
	import { lazyLoader } from '$lib/utils/lazyloader';
	import { LinkTo } from '$lib/components/atoms/Link';
	import { ListCarousel } from '$lib/components/organisms/Listing';
	import { Tab, TabList, TabPanel, Tabs } from '$lib/components/molecules/Tabs';
	import { Accordion } from '$lib/components/atoms/Accordion';
	import classNames from 'classnames';
	import { Button } from '$lib/components/atoms/Button';
	import { CompanyFlag, CourseSelect } from '$lib/components/atoms/Field';
	import { closeAllModals, openModal } from '$lib/utils/modals';
	import { Price } from '$lib/components/atoms/Price';
	import { BadgeLatestSeats, BadgePromoDetails } from '$lib/components/atoms/Badge';
	import { CFUCourseModal } from '$lib/components/organisms/Modal';
	import { Head } from '$lib/components/atoms/Head';
	import { isPriceWithIva } from '$lib/utils/price-utils';
	import { setTintColorByCourse } from '$lib/utils/color-utils';
  import { isMobileBrowser } from '$lib/utils/browser-utils';
	import { useCourseTypeUtils } from '$lib/utils/courses/type-utils';

	export let data: PageData;

	const {
		courseCategory,
		requestedSessionId,
		courseData,
		staticPageData,
		faq
	}: {
		courseCategory: ICourseCategoryFields;
		requestedSessionId: string;
		courseData: CourseModel;
		staticPageData: any;
		faq: FaqDataCourse;
	} = data;

	let reviews: IReviews[] = [];
	let course: CourseModel;

	const contentfulData = staticPageData;
	const metaData = contentfulData && contentfulData.length > 0 ? contentfulData[0] : null;

	const historyStore = useHistoryStore();
	const ctaFlow = useCTAFlow();
	const countdownStore = useCountdown();
	const displayCountdownLabel = countdownStore.displayLabel;

	try {
		course = CourseModel.fromJSON(courseData);
		reviews = [];
	} catch (e) {
		console.error('Warning: ', e);
	}

	let showCartContent: boolean = false;
	let selectedSession: SessionModel =
		requestedSessionId && course.sessions && course.sessions[requestedSessionId]
			? course.sessions[requestedSessionId]
			: course.getMainSession();

	$: selectedSession = selectedSession || course.getMainSession();

	let loading: boolean = false;

	const getTeachersForCarousel = () => {
		if (!selectedSession || !selectedSession.instructors) {
			return [];
		}

		return selectedSession.instructors.map((instructor) => {
			return {
				title: `${instructor.name} ${instructor.surname}`,
				subtitle: instructor.jobTitle,
				image: handleUrlClientSide(instructor.imageUrl),
				url: `/docenti/${stringToSlug(`${instructor.name} ${instructor.surname} ${instructor.idInstructor}`)}`
			};
		});
	};

	const getSessionPackages = () => {
		if (!selectedSession.packages) {
			return [];
		}

		return selectedSession.packages;
	};

	const getSessionOptions = () => {
		if (!selectedSession.options) {
			return [];
		}

		return selectedSession.options.filter(
			(option) => option.type.group?.toLowerCase() !== 'pacchetto'
		);
	};

	let teachers = getTeachersForCarousel();
	$: teachers = teachers || [];
	let sessionPackages = getSessionPackages();
	$: sessionPackages = sessionPackages || [];
	let sessionOptions = getSessionOptions();
	$: sessionOptions = sessionOptions || [];

	const tabData: {label: string, content: string}[] = [];

	const htmlLooksEmpty = (html: string): boolean => {
		if (browser) {
			const div = document.createElement('div');
			div.innerHTML = html;
			const text = div.textContent || div.innerText;
			return !/\S/.test(text);
		}
		return false; // TODO: added in SvelteKit
	};

	const hasContentFor = (field: 'admissionDetail' | 'creditDetail' | 'paymentDetail'): boolean => {
		const content = selectedSession[field];
		if (!content) {
			return false;
		}
		return !htmlLooksEmpty(content);
	};

	if (browser) {
		const tabFields = [
			{ key: 'admissionDetail', label: 'Modalità di ammissione' },
			{ key: 'creditDetail', label: 'Finanziamenti e borse di studio' },
			{ key: 'paymentDetail', label: 'Pagamento' }
		] as const;

		for (const { key, label } of tabFields) {
			if (hasContentFor(key)) {
				tabData.push({
					label,
					content: cleanCourseHtml(selectedSession[key])
				});
			}
		}
	}

	const goToSessionPage = (session: SessionModel) => {
		const title = `${session.title}. ${session.courseTypeFull} ${session.lessionMode}`;
		const url = `/formazione/${session.slug}/${session.idSession}`;

		window.history.pushState({}, title, url);

		selectedSession = session;
		showCartContent = false;
		closeAllModals();
		teachers = getTeachersForCarousel();
		sessionPackages = getSessionPackages();
		sessionOptions = getSessionOptions();

		GtmStore.pushEvent({
			pageCategory: 'product',
			Code: selectedSession.productCode || 'NOTSET',
			Naming: selectedSession.title,
			Price: selectedSession.priceNoVat.toString() || 'NOTSET'
		});

		GtmStore.pushProductPageEvent({
			category: `${stringToSlug(selectedSession.lessionMode)}/${stringToSlug(selectedSession.courseTypeFull)}/${stringToSlug(
				selectedSession.category
			)}`,
			id: selectedSession.productCode,
			list: 'producttype_page',
			name: selectedSession.title,
			price: selectedSession.discountPrice
				? selectedSession.discountPrice.toString()
				: selectedSession.priceNoVat.toString()
		});

		historyStore.addHistoryEntry('view', {
			rank: selectedSession.ranking,
			category: selectedSession.category,
			endDate: selectedSession.endDate,
			productCode: selectedSession.productCode,
			startDate: selectedSession.startDate
		});

		return Promise.resolve(true);
	};

	const onScheduleChange = async (e: CustomEvent<{ value: SessionModel }>) => {
		if (selectedSession.idSession !== e.detail.value.idSession) {
			goToSessionPage(e.detail.value);
		}
		closeAllModals();
	};

	let disableDownload: boolean = false;
	const onDownloadBrochure = () => {
		if (loading || disableDownload) {
			return;
		}

		disableDownload = true;
		loading = true;
		ctaFlow.startCTAFlow(
			$page.url.pathname,
			'downloadBrochure',
			ctaRequestBuilders.downloadBrochure(selectedSession, $page, {}, null)
		);
	};

	const onSendApplicationForm = async (event: Event) => {
		event.preventDefault();
		if (loading) {
			return;
		}
		loading = true;
		ctaFlow.startCTAFlow(
			$page.url.pathname,
			'sendApplicationForm',
			ctaRequestBuilders.sendApplicationForm(selectedSession, null)
		);
	};

	const onRequestInfo = async (event: Event) => {
		event.preventDefault();
		if (loading) {
			return;
		}
		loading = true;
		ctaFlow.startCTAFlow(
			$page.url.pathname,
			'requestInfo',
			ctaRequestBuilders.requestInfo(selectedSession, null)
		);
	};

	const onBuy = async (event: Event) => {
		event.preventDefault();
		if (loading) {
			return;
		}
		loading = true;
		ctaFlow.startCTAFlow($page.url.pathname, 'buy', ctaRequestBuilders.buy(selectedSession));
	};

	const metaTitleHandler = (): string => {
		if (metaData && metaData.fields && metaData.fields.metaTagSocialTitle) {
			return metaData.fields.metaTagSocialTitle;
		}
		return `${selectedSession.title.trim()} ${removeMasterCorso(course.courseTypeFull)}`;
	};

	const metaDescriptionHandler = (): string => {
		if (metaData && metaData.fields && metaData.fields.metaTagSocialDescription) {
			return metaData.fields.metaTagSocialDescription;
		}

		if (selectedSession.competencies && selectedSession.competencies.toLowerCase()) {
			return cleanCourseHtml(selectedSession.competencies).slice(0, 160);
		} else if (selectedSession.whoyouwillbe && selectedSession.whoyouwillbe.toLowerCase()) {
			return cleanCourseHtml(selectedSession.whoyouwillbe).slice(0, 160);
		}
		return null;
	};

	const metaImageHandler = (): string | null => {
		if (metaData && metaData.fields && metaData.fields.metaTagSocialImage) {
			return handleUrlClientSide(metaData.fields.metaTagSocialImage);
		}
		return selectedSession.previewUrl ? handleUrlClientSide(selectedSession.previewUrl) : null;
	};

	let courseWithCFUProductCode: string | null;
	let courseWithoutCFUProductCode: string | null;
	let gtmCFUEventPushed: boolean = false;
	let CFUCookieIsSet: boolean = false;

	const goToProductPage = (
		course: CourseModel | CourseLightModel | SessionModel | SessionLightModel
	) => {
		goto(`/formazione/${course.slug}`);
	};

	const onClickWithoutCFU = async () => {
		let choosedCourse: CourseModel | CourseLightModel | SessionModel | SessionLightModel | null =
			course;
		if (isCFUCourse(selectedSession.productCode)) {
			choosedCourse = await getCourseByProductCode(courseWithoutCFUProductCode, fetch);
			if (!gtmCFUEventPushed && !CFUCookieIsSet) {
				GtmStore.pushEvent({
					event: 'BBEvent',
					category: 'CFU',
					action: 'NO',
					label: 'CFU',
					event_id: '1',
					page_path: `/formazione/${choosedCourse.slug}`
				});
				gtmCFUEventPushed = true;
			}
			goToProductPage(choosedCourse);
		}

		if (!gtmCFUEventPushed && !CFUCookieIsSet) {
			GtmStore.pushEvent({
				event: 'BBEvent',
				category: 'CFU',
				action: 'NO',
				label: 'CFU',
				event_id: '1',
				page_path: `/formazione/${choosedCourse.slug}`
			});
			gtmCFUEventPushed = true;
		}
	};

	const onClickWithCFU = async () => {
		let choosedCourse: CourseModel | CourseLightModel | SessionModel | SessionLightModel | null =
			course;
		if (!isCFUCourse(selectedSession.productCode)) {
			choosedCourse = await getCourseByProductCode(courseWithCFUProductCode, fetch);
			if (!gtmCFUEventPushed && !CFUCookieIsSet) {
				GtmStore.pushEvent({
					event: 'BBEvent',
					category: 'CFU',
					action: 'SI',
					label: 'CFU',
					event_id: '1',
					page_path: `/formazioni/${choosedCourse.slug}`
				});
				gtmCFUEventPushed = true;
			}
			goToProductPage(choosedCourse);
		}
		if (!gtmCFUEventPushed && !CFUCookieIsSet) {
			GtmStore.pushEvent({
				event: 'BBEvent',
				category: 'CFU',
				action: 'SI',
				label: 'CFU',
				event_id: '1',
				page_path: `/formazione/${choosedCourse.slug}`
			});
			gtmCFUEventPushed = true;
		}
	};

	const pageLoaded = () => {
		if (courseHasCFUCourse(selectedSession.productCode)) {
			if (isCFUCourse(selectedSession.productCode)) {
				courseWithCFUProductCode = selectedSession.productCode;
				courseWithoutCFUProductCode = getCourseWithoutCFUs(selectedSession.productCode);
			} else {
				courseWithCFUProductCode = getCourseWithCFUs(selectedSession.productCode);
				courseWithoutCFUProductCode = selectedSession.productCode;
			}

			// const CFUCookie: 'with' | 'without' | null = getCFUCookie();
			const CFUCookie: 'with' | 'without' | null = null;
			if (CFUCookie) {
				CFUCookieIsSet = true;
				if (CFUCookie === 'with') {
					onClickWithCFU();
				} else if (CFUCookie === 'without') {
					onClickWithoutCFU();
				}
				return;
			}
			GtmStore.pushEvent({
				event: 'pageview',
				pageCategory: 'popup_cfu',
				page_path: window.location.pathname
			});

			openModal(CFUCourseModal, {
				title: removeMasterCorso(
					course.title,
					selectedSession.productCode ? selectedSession.productCode : ''
				),
				onClickWithCFU,
				onClickWithoutCFU
			});

			return;
		}
	};

	customPageOptions({
		headerFixed: false,
		headerModal: false,
		bodyStyle: 'gray',
		applyOrientationModalVisible: false
	});

	onMount(async () => {
		await setTintColorByCourse(selectedSession);

		const courseTypeUtils = useCourseTypeUtils(fetch);
		courseTypeUtils
			.getCourseTypeByName(selectedSession.courseTypeFull)
			.then((courseTypeResponse) => {
				courseType = courseTypeResponse;
			})
			.catch((error) => {
				console.error('Error fetching course type:', error);
			});

		pageLoaded();
		historyStore.addHistoryEntry('view', {
			rank: selectedSession.ranking,
			category: selectedSession.category,
			endDate: selectedSession.endDate,
			productCode: selectedSession.productCode,
			startDate: selectedSession.startDate
		});

		GtmStore.pushEvent({
			pageCategory: 'product',
			Code: selectedSession.productCode || 'NOTSET',
			Naming: selectedSession.title,
			Price: selectedSession.priceNoVat || 'NOTSET'
		});

		GtmStore.pushProductPageEvent({
			category: `${stringToSlug(selectedSession.lessionMode)}/${stringToSlug(selectedSession.courseTypeFull)}/${stringToSlug(
				selectedSession.category
			)}`,
			id: selectedSession.productCode,
			list: 'producttype_page',
			name: selectedSession.title,
			price: selectedSession.discountPrice
				? selectedSession.discountPrice.toString()
				: selectedSession.priceNoVat.toString()
		});
	});

	const courseJSON = StructureUtils.getCourseStructure(course, true);

	let ldScriptTag: string = '';

	if (!browser) {
		let jsons: any[] = [courseJSON];

		if (PUBLIC_FTOGGLE_PRODUCT_SOCIAL_RATING === 'true') {
			const aggregateRatingJSON = StructureUtils.getAggregateStructure(
				metaTitleHandler(),
				metaDescriptionHandler(),
				true
			);
			jsons.push(aggregateRatingJSON);
		}

		ldScriptTag = StructureUtils.createScriptTag(JSON.stringify(jsons));
	}

	// let isCourseOfTypeLaurea: boolean = false;
	// $: isCourseOfTypeLaurea = LaureaUtils.isCourseOfTypeLaurea(course);

	let courseType: ICourseTypeFields;

	let hasFaq: boolean = false;
	$: hasFaq =
		faq &&
		selectedSession &&
		faq[selectedSession.idSession] &&
		faq[selectedSession.idSession].length > 0;

	let seoDetails;
	$: seoDetails = {
		title: metaTitleHandler(),
		description: metaDescriptionHandler(),
		url: `/formazione/${course.slug}`,
		canonical: `/formazione/${course.slug}`,
		image: metaImageHandler(),
		ldScriptTag
	};
</script>

<Head appendCompany {seoDetails} />

<!-- {#if isCourseOfTypeLaurea}
  <CorsoLaureaPage
    bind:course={course}
    bind:selectedSession={selectedSession}
    {courseCategorySlug}
    bind:courseSlug={courseSlug}
    bind:hasFaq={hasFaq}
    bind:faq={faq}
  />
{:else if course} -->
{#if course}
	<Breadcrumb disableLastElementLink>
		<BreadcrumbItem>
			<a href="/" title="Home">Home</a>
		</BreadcrumbItem>
		<BreadcrumbItem>
			<a href="/{courseCategory.slug}" title={courseCategory.categoryName}>{courseCategory.categoryName}</a>
		</BreadcrumbItem>
		<BreadcrumbItem current>
			<a href="" title={course.title}>{course.title}</a>
		</BreadcrumbItem>
	</Breadcrumb>

	<HeroProduct {course} session={selectedSession} />

	<Grid>
		<section class="product-sub-grid">
			<main
				class="main-container"
			>
				{#key selectedSession.idSession}
					<AnchorTabs>
						<a href="#programma">Programma</a>
						{#if sessionPackages && sessionPackages.length > 0}
							<a href="#piani">Piani</a>
						{/if}
						{#if selectedSession.certificationList && selectedSession.certificationList.length > 0}
							<a href="#certificati">Attestati</a>
						{/if}
						{#if reviews && reviews.length > 0}
							<a href="#testimonianze">Testimonianze</a>
						{/if}
						{#if teachers && teachers.length > 0}
							<a href="#docenti">Docenti</a>
						{/if}
						{#if hasContentFor('admissionDetail') || hasContentFor('creditDetail') || hasContentFor('paymentDetail')}
							<a href="#info">Info utili</a>
						{/if}
						{#if hasFaq}
							<a href="#faq">FAQ</a>
						{/if}
					</AnchorTabs>
				{/key}

				{#if selectedSession}
					{#if selectedSession.ambassador && selectedSession.ambassador.name && selectedSession.ambassador.lastname && selectedSession.ambassador.title && selectedSession.ambassador.testimony && selectedSession.ambassador.image}
						<SectionProduct class="py-8" id="programma">
							<div class="grid grid-cols-1 md:grid-cols-2 gap-8">
								{#if selectedSession.ambassador.image}
									<img
										src={handleUrlClientSide(selectedSession.ambassador.image)}
										alt={`${selectedSession.ambassador.name} ${selectedSession.ambassador.lastname}`}
										class="w-full"
									/>
								{/if}
								<div class="md:pl-8">
									<h2 class="text-xl uppercase">
										{`${selectedSession.ambassador.name} ${selectedSession.ambassador.lastname}`}
									</h2>
									<p class="subtitle v-small mb-2">
										{selectedSession.ambassador.title}
									</p>
									<p class="mb-4">
										{cleanCourseHtml(selectedSession.ambassador.testimony)}
									</p>
								</div>
							</div>
						</SectionProduct>
					{/if}
					<SectionProduct variant="transparent" id="programma">
						<CourseFeatures
							title={selectedSession.competencies &&
							selectedSession.competencies.toLowerCase() != 'null'
								? cleanCourseHtml(selectedSession.competencies)
								: ''}
							description={selectedSession.whoyouwill &&
							selectedSession.whoyouwill.toLowerCase() != 'null'
								? cleanCourseHtml(selectedSession.whoyouwill)
								: ''}
							thereIsAmbassador={!!(
								selectedSession.ambassador &&
								selectedSession.ambassador.name &&
								selectedSession.ambassador.lastname &&
								selectedSession.ambassador.title &&
								selectedSession.ambassador.testimony &&
								selectedSession.ambassador.image
							)}
						>
							<!-- The components show this only if title AND description are empty -->
							<p slot="headernotes">
								{@html selectedSession.reasonwhy &&
								selectedSession.reasonwhy.toLowerCase() != 'null'
									? cleanCourseHtml(selectedSession.reasonwhy)
									: ''}
							</p>

							<svelte:fragment slot="boxes">
								{#if !isEuroconferenceSession(selectedSession)}
									<FeatureBoxesByCourseType
										{courseType}
									/>										
								{/if}
							</svelte:fragment>
							<!-- The components show this only if title OR description are NOT empty -->
							<p slot="footnotes">
								{@html selectedSession.reasonwhy &&
								selectedSession.reasonwhy.toLowerCase() != 'null'
									? cleanCourseHtml(selectedSession.reasonwhy)
									: ''}
							</p>
						</CourseFeatures>
					</SectionProduct>
				{/if}

				{#if sessionPackages.length > 0 && sessionOptions.length > 0}
					<SectionProduct id="piani" variant="transparent" class="py-6 md:pt-14 md:pb-20">
						<p class="subtitle v-small mb-2">
							{sessionPackages.length === 1
								? 'Scopri tutti i servizi ancillari'
								: 'Personalizza il tuo master'}
						</p>
						{#if false}
							<h3 class="mb-8">
								{sessionPackages.length === 1
									? 'Personalizza il tuo percorso formativo'
									: 'Piani del corso'}
							</h3>
						{/if}
						<p>
							Per arricchire al massimo la tua esperienza formativa, ti offriamo una varietà di
							servizi premium aggiuntivi da scegliere in aggiunta al programma del master.
						</p>

						<div class="mt-8">
							<div class="hidden md:block">
								<CoursePackageTableDesktop
									bind:selectedSession
									bind:packages={sessionPackages}
									bind:options={sessionOptions}
									hasVatFree={hasVatFreeSession(course)}
									showPriceWithIva={isPriceWithIva(selectedSession.productCode)}
								/>
							</div>
							<div class="block md:hidden">
								<CoursePackageTableMobile
									bind:selectedSession
									bind:packages={sessionPackages}
									bind:options={sessionOptions}
									hasVatFree={hasVatFreeSession(course)}
									showPriceWithIva={isPriceWithIva(selectedSession.productCode)}
								/>
							</div>
						</div>
					</SectionProduct>
				{/if}

				{#if course.sessions && Object.keys(course.sessions).length > 0}
					<SectionProduct class="py-6 md:pt-14 md:pb-20">
						<p class="subtitle v-small mb-2">Scopri le date di partenza</p>
						<h3>Quando iniziare</h3>

						<div class="mt-8">
							<CourseScheduleSelector
								on:change={onScheduleChange}
								sessions={course.sessions}
								{selectedSession}
								hasVatFree={hasVatFreeSession(course)}
							/>
						</div>
					</SectionProduct>

					<SectionProduct variant="transparent" class="py-6 md:py-14">
						<CourseLearningFeatures session={selectedSession} />
					</SectionProduct>
				{/if}

				{#if selectedSession.modulesList && selectedSession.modulesList.length > 0}
					<SectionProduct variant="transparent" class="py-6 md:pt-14 md:pb-20">
						<svelte:fragment slot="header">
							<p class="subtitle v-small mb-2">I passi per raggiungere l'obiettivo</p>
							<h3>Cosa imparerai</h3>
						</svelte:fragment>
						<MosaicCourseModules showExpandButton={selectedSession.modulesList.length > (isMobileBrowser() ? 3 : 6)}>
							{#each selectedSession.modulesList as module, index}
								<CourseModuleCard subtitle="Modulo {index + 1}" title={module} />
							{/each}
						</MosaicCourseModules>
					</SectionProduct>
				{/if}

				{#if selectedSession.contributorPartners && selectedSession.contributorPartners.length > 0}
					<SectionProduct variant="transparent" class="py-6 md:py-14">
						<svelte:fragment slot="header">
							<p class="subtitle v-small mb-2">Il valore della Partnership</p>
							<h3>Un’alleanza strategica</h3>
						</svelte:fragment>
						<MosaicCoursePartners>
							{#each selectedSession.contributorPartners as contributorPartner}
								<CoursePartnerCard lazyLoad={true} partner={contributorPartner} />
							{/each}
						</MosaicCoursePartners>
					</SectionProduct>
				{/if}

				{#if selectedSession.collaborationPartners && selectedSession.collaborationPartners.length > 0}
					<SectionCarousel mobileCount={1} desktopCount={1} duration={0}>
						<SectionProduct variant="transparent" class="py-6 md:pt-14 md:pb-20">
							<svelte:fragment slot="header">
								<p class="subtitle v-small mb-3 md:mb-7">I partners del master</p>
								<h3 class="md:max-w-md mb-8 md:mb-12 lg:mb-18">
									Collabora con l’eccellenza per avere un’esperienza continuativa nelle più grandi
									realtà del mercato
								</h3>
							</svelte:fragment>
							<svelte:fragment slot="indicators">
								<SectionCarouselIndicators />
							</svelte:fragment>
							<SectionCarouselSlides>
								<CoursePartners>
									{#each selectedSession.collaborationPartners.slice(0, 8) as partner}
										<a href={`/partners/${partner.slug}`}>
											<img
												use:lazyLoader={handleUrlClientSide(partner.imageUrl)}
												alt={partner.name}
											/>
										</a>
									{/each}
								</CoursePartners>
								{#if selectedSession.collaborationPartners.length > 8}
									<CoursePartners>
										{#each selectedSession.collaborationPartners.slice(8, 16) as partner}
											<a href={`/partners/${partner.slug}`}>
												<img
													use:lazyLoader={handleUrlClientSide(partner.imageUrl)}
													alt={partner.name}
												/>
											</a>
										{/each}
									</CoursePartners>
								{/if}
								{#if selectedSession.collaborationPartners.length > 16}
									<CoursePartners>
										{#each selectedSession.collaborationPartners.slice(16, 24) as partner}
											<a href={`/partners/${partner.slug}`}>
												<img
													use:lazyLoader={handleUrlClientSide(partner.imageUrl)}
													alt={partner.name}
												/>
											</a>
										{/each}
									</CoursePartners>
								{/if}
							</SectionCarouselSlides>
						</SectionProduct>
					</SectionCarousel>
				{/if}

				{#if selectedSession && ['Master Full Time con Stage', 'Master Full Time con Career Service', 'Master Part Time'].includes(selectedSession.courseTypeFull)}
					<SectionCertificates id="certificati">
						<svelte:fragment slot="header">
							<p class="subtitle v-small v-white">CERTIFICATI E BADGE DIGITALI</p>
							<h4>
								Questo percorso ti garantisce di ottenere il certificato e il badge digitale di
								24ORE Business School
							</h4>
							<p>
								Un riconoscimento dell’impegno e della dedizione che metterai in questo tuo
								importante cammino. Potrai condividerlo con il mondo tramite i tuoi profili social e
								avrai l'opportunità di mostrare il tuo coinvolgimento a potenziali datori di lavoro,
								amici e familiari, e anche di inserirlo direttamente nella firma delle tue e-mail.
							</p>
						</svelte:fragment>

						<CourseCertificates
							title="A CONCLUSIONE DEL MASTER RICEVERAI QUESTI DUE IMPORTANTI RICONOSCIMENTI"
						>
							<CertificateCard
								lazyLoad={true}
								image="/placeholders/certification/certificato_digitale.png"
								srcset="/placeholders/certification/certificato_digitale.png"
								imageAlt="Certificato Digitale"
								title="Certificato Digitale"
							/>
							<CertificateCard
								lazyLoad={true}
								image="/placeholders/certification/badge_digitale.png"
								srcset="/placeholders/certification/badge_digitale.png"
								imageAlt="Badge Digitale"
								title="Badge Digitale"
							/>
							<p slot="footer">
								<span>Riceverai gli attestati in forma digitale da poter condividere su</span>
								<span
									><img
										class="inline h-[36px] mx-1"
										use:lazyLoader={'/placeholders/certification/linkedin.png'}
										alt="LinkedIn"
									/></span
								>
								<span>per affacciarti al mondo del lavoro.</span>
							</p>
						</CourseCertificates>
					</SectionCertificates>
				{/if}

				{#if reviews && reviews.length > 0}
					<SectionProduct compact variant="transparent" class="py-6 md:pt-6 md:pb-14">
						<svelte:fragment slot="header">
							<p class="subtitle v-small mb-2">Dicono di noi</p>
						</svelte:fragment>
						<MosaicCourseReviews>
							<CourseReviewCard hideCourseTitle truncateTitle truncateBody review={reviews[0]} />
							<CourseReviewCard hideCourseTitle truncateTitle truncateBody review={reviews[1]} />
						</MosaicCourseReviews>

						<LinkTo slot="footer" href="/reviews">Vedi tutte le recensioni</LinkTo>
					</SectionProduct>
				{/if}

				{#if teachers && teachers.length > 0}
					<SectionProduct id="docenti" class="py-6 md:py-14">
						<svelte:fragment slot="header">
							<p class="subtitle v-small mb-2">I nostri docenti</p>
							<h3>Lasciati guidare</h3>
						</svelte:fragment>

						<ListCarousel data={teachers} />
					</SectionProduct>
				{/if}

				{#if hasContentFor('admissionDetail') || hasContentFor('creditDetail') || hasContentFor('paymentDetail')}
					<SectionProduct id="info" variant="transparent" class="pt-6 md:pt-14">
						<svelte:fragment slot="header">
							<p class="subtitle v-small mb-2">Informazioni utili</p>
							<h3>Tutto ciò che serve per iniziare</h3>
						</svelte:fragment>
						<Tabs context="page-container">
							<TabList>
								{#if hasContentFor('admissionDetail')}
									<Tab size="compact">Modalità di ammissione</Tab>
								{/if}
								{#if hasContentFor('creditDetail')}
									<Tab size="compact">Finanziamenti e borse di studio</Tab>
								{/if}
								{#if hasContentFor('paymentDetail')}
									<Tab size="compact">Pagamento</Tab>
								{/if}
							</TabList>

							<div class="product-tabs-container">
								{#if hasContentFor('admissionDetail')}
									<TabPanel>
										<!-- START Content of the current site -->
										{@html cleanCourseHtml(selectedSession.admissionDetail)}
										<!-- END Content of the current site -->
									</TabPanel>
								{/if}
								{#if hasContentFor('creditDetail')}
									<TabPanel>
										<!-- START Content of the current site -->
										{@html cleanCourseHtml(selectedSession.creditDetail)}
										<!-- END Content of the current site -->
									</TabPanel>
								{/if}
								{#if hasContentFor('paymentDetail')}
									<TabPanel>
										<!-- START Content of the current site -->
										{@html cleanCourseHtml(selectedSession.paymentDetail)}
										<!-- END Content of the current site -->
									</TabPanel>
								{/if}
							</div>
						</Tabs>
					</SectionProduct>
				{/if}

				{#if hasFaq}
					<SectionProduct id="faq" variant="transparent" class="py-6 md:pt-14 md:pb-6 bg-tint text-contrast-tint">
						<Grid>
							<div class="col-span-full md:col-span-4">
								<p class="subtitle v-small mb-2 text-white">FAQ</p>
								<p class="section-title mb-2 text-white">Domande frequenti</p>
							</div>
							<div class="col-span-full md:col-span-8">
								{#key selectedSession.idSession}
									<SectionFaq>
										{#each faq[selectedSession.idSession] as currentFaq}
											<Accordion
												variant="light"
												entry={{
													question: currentFaq.question,
													answer: currentFaq.answerHtml
												}}
												offset={60}
											/>
										{/each}
									</SectionFaq>
								{/key}
							</div>
						</Grid>
					</SectionProduct>
				{/if}
			</main>
			<aside
				class={classNames('course-cart', {
					stickToPromoLabel: $displayCountdownLabel
				})}
			>
				<SideCourseCart session={selectedSession} bind:showCartContent stickyTarget="#programma">
					<svelte:fragment slot="header">
						{#if (selectedSession.admissionForm || selectedSession.admissionWithBuyAvailable === '1' || selectedSession.admissionWithBuyAvailable === '2') && !selectedSession.soldOut}
							<Button
								color="pink"
								on:click={onSendApplicationForm}
								isLoading={loading}
								disabled={loading}
							>
								{isMarconiSession(selectedSession) ? 'ACQUISTA' : 'DOMANDA DI AMMISSIONE'}</Button
							>
						{:else if selectedSession.buyButtonAvailable}
							<Button
								color="pink"
								on:click={onBuy}
								isLoading={loading}
								disabled={loading}
							>
								Acquista
							</Button>
						{/if}
						{#if selectedSession.brochureAvailable && selectedSession.brochureUrl}
							<Button
								icon="download"
								color="pink"
								on:click={onDownloadBrochure}
								isLoading={loading}
								disabled={loading || disableDownload}
							>
								Download Brochure
							</Button>
						{:else if selectedSession.brochureBooking}
							<Button
								icon="download"
								color="pink"
								on:click={onDownloadBrochure}
								isLoading={loading}
								disabled={loading}
							>
								Prenota Brochure
							</Button>
						{/if}
						<Button
							icon="email"
							color="white"
							variant="fill"
							on:click={onRequestInfo}
							isLoading={loading}
							disabled={loading}
						>
							Richiedi informazioni
						</Button>
					</svelte:fragment>
					<svelte:fragment slot="company-flag">
						<CompanyFlag class="text-black" />
					</svelte:fragment>
					<svelte:fragment slot="course-date-price">
						{#if course.sessions}
							<CourseSelect
								session={selectedSession}
								sessionOptions={course.sessions}
								bind:selectedSession
								hasVatFree={hasVatFreeSession(course)}
								showExpandButton={false}
								enableSessionSelection={courseType?.slug !== 'corsi-online' && course.sessions && Object.keys(course.sessions).length > 0}
								{onScheduleChange}
							/>

							{#if selectedSession}
								<Price
									location="sidebar"
									session={selectedSession}
									showOnSaleDetail={true}
									hasVatFree={hasVatFreeSession(course)}
								/>
								{#if selectedSession.lastSeats && !selectedSession.soldOut}
									<BadgeLatestSeats fullwidth session={selectedSession} />
								{:else if selectedSession.promoDetailLabel}
									<BadgePromoDetails fullwidth session={selectedSession} />
								{/if}
							{/if}
						{/if}
					</svelte:fragment>
					<svelte:fragment slot="course-info">
						<SideCourseData session={selectedSession} />
					</svelte:fragment>

					<svelte:fragment slot="sticky-footer">
						{#if (selectedSession.admissionForm || selectedSession.admissionWithBuyAvailable === '1' || selectedSession.admissionWithBuyAvailable === '2') && !selectedSession.soldOut}
							<Button
								color="pink"
								on:click={onSendApplicationForm}
								isLoading={loading}
								disabled={loading}
							>
								{isMarconiSession(selectedSession) ? 'ACQUISTA' : 'DOMANDA DI AMMISSIONE'}
							</Button>
						{:else if selectedSession.buyButtonAvailable && !selectedSession.soldOut}
							<Button
								color="pink"
								on:click={onBuy}
								isLoading={loading}
								disabled={loading}
							>
								Acquista
							</Button>
						{/if}
						{#if selectedSession.brochureAvailable && selectedSession.brochureUrl}
							<Button
								icon="download"
								color="pink"
								on:click={onDownloadBrochure}
								isLoading={loading}
								disabled={loading}
							>
								Download Brochure
							</Button>
						{:else if selectedSession.brochureBooking}
							<Button
								icon="download"
								color="pink"
								on:click={onDownloadBrochure}
								isLoading={loading}
								disabled={loading}
							>
								Prenota Brochure
							</Button>
						{/if}
						<Button
							icon="email"
							color="white"
							variant="fill"
							on:click={onRequestInfo}
							isLoading={loading}
							disabled={loading}
						>
							Richiedi informazioni
						</Button>
					</svelte:fragment>
				</SideCourseCart>
			</aside>
		</section>
	</Grid>
{/if}

<style type="text/scss" lang="scss" scoped>
	.product-sub-grid {
		--sidebar-min-width: 280px;
		--sidebar-max-width: 400px;

		@apply flex flex-col-reverse col-span-full lg:grid;

		@screen lg {
			grid-template-columns: repeat(3, minmax(0, 1fr)) var(--sidebar-min-width);
			grid-template-areas: 'main main main sidebar';
			gap: var(--page-grid-gap);
		}
	}

	:global(.product-sub-grid ol, .product-sub-grid ul) {
		list-style: disc;
		margin: 0;
		padding-left: 1em;
	}

	.main-container {
		@apply col-span-full row-start-2 lg:row-start-1;
		grid-area: main;
	}

	.course-cart {
		@apply col-span-full lg:col-span-3 row-start-1;
		@apply h-auto lg:h-screen;
		@apply lg:sticky;
		@apply z-over-sticky-element md:top-0;

		@apply bg-white;
		@apply lg:shadow-fake-x-border;
		@apply lg:border-b lg:border-gray-300 lg:border-opacity-50;
		/*@apply max-w-md;*/
		grid-area: sidebar;
		max-width: 100%;
		margin: 0;
		@apply lg:-page-margin lg:xs:mx-auto lg:lg:mx-0;

		@apply lg:-page-margin lg:xs:mx-auto lg:lg:mx-0;

		/* Remove page margin to garantuee the exact alignment with HeroProduct */
		@screen lg {
			min-width: 306px;
			min-height: 1000px;
			max-width: none;
			margin-right: calc(var(--page-margin) * -1);
			margin-left: auto;
		}
		&.stickToPromoLabel {
			top: var(--promo-height);
		}
	}

	.product-tabs-container {
		@apply relative;
		@apply bg-white page-margin;
		@apply py-6 md:pt-14 md:pb-24;
		&:before {
			content: '';
			@apply md:block;
			@apply md:bg-white;
			@apply md:absolute inset-y-0 left-0;

			@screen md {
				width: calc((100vw - var(--page-current-width)) / 2 + var(--page-margin));
				margin-left: calc((100vw - var(--page-current-width)) * -0.5 - var(--page-margin));
			}
		}
	}

	.free-cancel {
		list-style: none;
		margin: 0;
		padding-left: 1em;
		li {
			@apply flex text-safegreen items-center;

			.data-icon {
				@apply inline-flex flex-shrink-0 self-start mr-3;

				&.info {
					@apply flex;
					@apply ml-2;
					@apply text-courses-default;
					@apply cursor-pointer;
				}
			}

			.data-key {
				@apply uppercase tracking-wide text-xs md:text-sm;
			}

			.data-value {
				@apply text-xs md:text-sm;
			}

			.data-key + .data-value {
				@apply mt-1;
			}
		}
	}
</style>
